import { ContactFields, FormContactFieldsType, useAuth0 } from '@brighte/passwordless-authenticator';
import { ApplicantType, CodeAcceptanceStatus, contactDetailsSchema } from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { datadogRum } from '@datadog/browser-rum';
import { useHandlePostApplicationLoginMutation } from '@graphql/index';
import { useApplicationData, useSetApplicationData } from '@provider';
import { APPLICATION_STEPS, CONTACT_DETAILS, getTrackingId, isJointApplicant } from '@utils';
import { useContactDetailsSummary } from './useContactDetailsSummary';

const ContactDetails = () => {
  const { TITLE } = CONTACT_DETAILS;
  const contactDetailsSummary = useContactDetailsSummary();
  const prePopContactDetails = useApplicationData('ContactDetails');
  const { quote } = useApplicationData(['quote']);
  const setApplicationData = useSetApplicationData();
  const { user } = useAuth0();
  const { mutate } = useHandlePostApplicationLoginMutation();

  const handleTracking = () => {
    if (!user?.uid) return;
    datadogRum.addAction('Signed In', {
      brighte_id: user.userId,
      role_type: user.role,
    });
  };

  const handleVerificationSuccess = ({
    verifiedEmail,
    verifiedMobile,
  }: {
    verifiedEmail?: string;
    verifiedMobile: string;
  }) => {
    setApplicationData({ ContactDetails: { email: verifiedEmail, mobile: verifiedMobile } });
    mutate({
      postLoginInput: {
        applicantType: isJointApplicant() ? ApplicantType.Joint : ApplicantType.Primary,
        opportunityId: quote?.opportunity?.id,
        trackingId: getTrackingId(),
      },
    });
  };

  const handleError = (error: unknown) => {
    throw error;
  };

  return (
    <StepNavigator<FormContactFieldsType>
      schema={contactDetailsSchema}
      name={APPLICATION_STEPS.CONTACT_DETAILS}
      title={TITLE}
      defaultValues={{
        ...prePopContactDetails,
        newEmail: prePopContactDetails?.email,
        newMobile: prePopContactDetails?.mobile,
        otp: undefined,
        otpSent: false,
        codeAccepted: CodeAcceptanceStatus.NotAccepted,
        updateOtpSent: false,
      }}
      isLoading={false}
      isEditable={false}
      completedContent={contactDetailsSummary}
      hideSubmitButton={true}
    >
      {() => {
        if (!prePopContactDetails) {
          return null;
        }

        return (
          <ContactFields
            verificationDetails={{
              email: prePopContactDetails.email,
              mobile: prePopContactDetails.mobile,
              verificationType: prePopContactDetails.verificationType,
              maskedEmail: prePopContactDetails.maskedEmail,
              maskedMobile: prePopContactDetails.maskedMobile,
            }}
            onTracking={handleTracking}
            onError={handleError}
            onSuccess={handleVerificationSuccess}
            financialProductId={quote?.financialProduct?.id}
          />
        );
      }}
    </StepNavigator>
  );
};

export { ContactDetails };
